import { Body, Button, H5, Stack, useTheme } from '@qred/components-library';
import React from 'react';
import { ApplicationEvaluationOutcome } from '~/enums';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import useTranslate from '~/hooks/useTranslate';
import { isInIframe } from '~/iframeMode';
import { cardApplicationSteps } from '~/pages/CardApplication/components/Steps/cardApplicationSteps';
import { trackCardApplicationActions } from '~/store/actions/analyticsActions';
import { StandardPageContainer } from '~/styles/BaseStyledComponents';

type ImgConfig = {
  src: string;
  dimensions: {
    width: number;
    height: number;
  };
};

const RESULT_IMG: Record<ApplicationEvaluationOutcome, ImgConfig> = {
  [ApplicationEvaluationOutcome.Approve]: {
    src: '/illustrations/applicationAndSubmit/confirmation.svg',
    dimensions: { width: 240, height: 240 },
  },
  [ApplicationEvaluationOutcome.ManualReview]: {
    src: '/illustrations/general/process.svg',
    dimensions: { width: 240, height: 240 },
  },
  [ApplicationEvaluationOutcome.Reject]: {
    src: '/illustrations/general/error.png',
    dimensions: { width: 180, height: 180 },
  },
};

export const CardApplicationResult = ({
  status,
  applicationUuid,
}: {
  status: ApplicationEvaluationOutcome;
  applicationUuid: string;
}) => {
  const theme = useTheme();
  const t = useTranslate();
  const dispatch = useAppDispatch();

  // Keyed by applicationUuid like the emailed offer links; the page resolves the client itself.
  const offerUrl = `${globalThis.location.origin}/card-offer/${applicationUuid}`;

  const handleViewOfferClick = () => {
    dispatch(
      trackCardApplicationActions({
        actionName: 'view_offer_button_clicked',
        extraContext: {
          stepName: 'processing_application',
          stepNumber: cardApplicationSteps.length + 1,
        },
      })
    );
  };

  const titleKey = {
    [ApplicationEvaluationOutcome.Approve]:
      'Onboarding.AutoProcessingApprovedTitle',
    [ApplicationEvaluationOutcome.ManualReview]:
      'Onboarding.AutoProcessingManualReviewTitle',
    [ApplicationEvaluationOutcome.Reject]:
      'Onboarding.AutoProcessingRejectedTitle',
  }[status];

  const descriptionKey = {
    [ApplicationEvaluationOutcome.Approve]:
      'Onboarding.AutoProcessingApprovedDescription',
    [ApplicationEvaluationOutcome.ManualReview]:
      'Onboarding.AutoProcessingManualReviewDescription',
    [ApplicationEvaluationOutcome.Reject]:
      'Onboarding.AutoProcessingRejectedDescription',
  }[status];

  const title = t(titleKey) as string;
  const description = t(descriptionKey);
  const img = RESULT_IMG[status];

  return (
    <StandardPageContainer
      $isInIframe={isInIframe}
      $background={
        isInIframe
          ? undefined
          : theme.fn.themeColor('Container/Option_2_(White)')
      }
      style={{
        padding: '79px 32px',
        justifyContent: 'center',
      }}
      data-testid="card_application_result_page"
    >
      <Stack align="center" spacing="xxl" maw="600px">
        <img
          src={img.src}
          alt={title}
          style={{
            width: `${img.dimensions.width}px`,
            height: `${img.dimensions.height}px`,
          }}
        />
        <H5 color="Text/Headlines_(Brand)">{title}</H5>
        <Body size="lg" color="Text/Secondary">
          {description}
        </Body>
        {status === ApplicationEvaluationOutcome.Approve && (
          <a
            href={offerUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleViewOfferClick}
          >
            <Button fullWidth size="md" rightIcon="open_in_new">
              {t('CreditCardTermsAndConditionsCard.IdentifyButtonText')}
            </Button>
          </a>
        )}
      </Stack>
    </StandardPageContainer>
  );
};
